/**
 * Per-link, per-tab visit state.
 *
 * Opening a client link produces two "only once for this link, in this tab"
 * facts: the photographer's brief has been dismissed, and the studio has been
 * told the client actually viewed the gallery. Both are keyed by share token so
 * a second link opened in the same tab starts clean, and both live in
 * sessionStorage so they survive a route change or a reload. Gallery engagement
 * resets for a fresh document navigation, including a tab with copied storage.
 */
import { trackShareEvent } from '../api/client';

/**
 * Flags a private-mode browser refused to store. sessionStorage stays the
 * source of truth; this only catches the case where it throws, so the guard
 * still holds for the life of the page instead of forgetting on every render.
 */
const refusedByStorage = new Set<string>();
const initializedVisits = new Set<string>();

function readFlag(key: string): boolean {
  try {
    if (window.sessionStorage.getItem(key) === '1') return true;
  } catch {
    /* fall through to the in-page set */
  }
  return refusedByStorage.has(key);
}

function writeFlag(key: string): void {
  try {
    window.sessionStorage.setItem(key, '1');
    return;
  } catch {
    /* storage refused — remember it for this page instead */
  }
  refusedByStorage.add(key);
}

function briefKey(shareToken: string | null): string {
  return `shotselect-brief-seen-${shareToken ?? 'unknown'}`;
}

function galleryViewedKey(shareToken: string): string {
  return `shotselect-gallery-viewed-${shareToken}`;
}

/** Has this tab already dismissed the brief for this link? */
export function hasSeenBrief(shareToken: string | null): boolean {
  return readFlag(briefKey(shareToken));
}

export function markBriefSeen(shareToken: string | null): void {
  writeFlag(briefKey(shareToken));
}

/**
 * Tell the studio the client looked at the gallery — at most once per link,
 * per tab.
 *
 * `gallery_viewed` is the studio's ENGAGEMENT signal: its link tracking renders
 * it as "Viewed gallery", distinct from the "Opened link" the worker already
 * records server-side for every manifest fetch. The gallery now renders the
 * instant a link opens, so mounting it is no longer evidence of anything — the
 * caller decides when the client is really looking, and this guard keeps the
 * count one-per-client instead of one-per-route-change (the photo viewer can
 * send them back to the gallery).
 */
export function reportGalleryViewed(
  shareToken: string,
  metadata: Record<string, string | number | boolean | null>,
): void {
  const key = galleryViewedKey(shareToken);
  if (!initializedVisits.has(key)) {
    initializedVisits.add(key);
    // A fresh navigation can reuse/copy a tab's storage (including window.open).
    // Reload and back/forward are continuations; an actual new open is not.
    const navigation = performance.getEntriesByType?.('navigation')[0] as
      PerformanceNavigationTiming | undefined;
    if (navigation?.type === 'navigate') {
      try {
        window.sessionStorage.removeItem(key);
      } catch {
        /* page fallback */
      }
      refusedByStorage.delete(key);
    }
  }
  if (readFlag(key)) return;
  writeFlag(key);
  // Request retries carry this exact ID; distinct tabs/visits get a new one.
  void trackShareEvent(shareToken, 'gallery_viewed', { metadata, visitId: crypto.randomUUID() });
}
